import { Controller } from "@hotwired/stimulus"
import * as FilePond from 'filepond'
// Restore image preview plugin - needed for file item display
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'

export default class extends Controller {
  static targets = ["input", "uploadProgress", "uploadList", "errorDisplay", "errorList"]
  static values = { 
    url: String,
    maxFileSize: String,
    acceptedFileTypes: String,
    maxFiles: Number
  }

  connect() {
    // Register FilePond plugins
    FilePond.registerPlugin(
      FilePondPluginImagePreview,
      FilePondPluginImageExifOrientation,
      FilePondPluginFileValidateSize,
      FilePondPluginFileValidateType
    )

    // Prevent default browser drag and drop behavior
    this.preventDefaultDragDrop()

    // Handle fallback button click before FilePond loads
    this.setupFallbackButton()
    
    this.setupFilePond()
  }

  disconnect() {
    this.removeDefaultDragDropPrevention()
    
    if (this.pond) {
      this.pond.destroy()
      this.pond = null
    }
    
    if (this.inputTarget.filepond) {
      FilePond.destroy(this.inputTarget)
    }
  }

  preventDefaultDragDrop() {
    // Prevent default drag behaviors on the entire upload zone
    const uploadZone = this.element
    
    this.dragEventHandlers = {
      dragover: (e) => {
        e.preventDefault()
        e.stopPropagation()
      },
      dragenter: (e) => {
        e.preventDefault()
        e.stopPropagation()
      },
      dragleave: (e) => {
        e.preventDefault()
        e.stopPropagation()
      },
      drop: (e) => {
        e.preventDefault()
        e.stopPropagation()
        
        // Let FilePond handle the drop if it's ready
        if (this.pond && e.dataTransfer?.files?.length > 0) {
          const files = Array.from(e.dataTransfer.files)
          files.forEach(file => {
            // Check if file is already in FilePond
            const existingFiles = this.pond.getFiles()
            const isDuplicate = existingFiles.some(existingFile => 
              existingFile.file && 
              existingFile.file.name === file.name && 
              existingFile.file.size === file.size
            )
            
            if (!isDuplicate) {
              this.pond.addFile(file)
            }
          })
        }
      }
    }
    
    // Add event listeners
    Object.keys(this.dragEventHandlers).forEach(eventType => {
      uploadZone.addEventListener(eventType, this.dragEventHandlers[eventType])
    })
    
    // Also prevent default on document to catch any missed events
    this.documentHandlers = {
      dragover: (e) => e.preventDefault(),
      drop: (e) => e.preventDefault()
    }
    
    Object.keys(this.documentHandlers).forEach(eventType => {
      document.addEventListener(eventType, this.documentHandlers[eventType])
    })
  }

  removeDefaultDragDropPrevention() {
    if (this.dragEventHandlers) {
      const uploadZone = this.element
      Object.keys(this.dragEventHandlers).forEach(eventType => {
        uploadZone.removeEventListener(eventType, this.dragEventHandlers[eventType])
      })
    }
    
    if (this.documentHandlers) {
      Object.keys(this.documentHandlers).forEach(eventType => {
        document.removeEventListener(eventType, this.documentHandlers[eventType])
      })
    }
  }

  setupFallbackButton() {
    const fallbackButton = this.element.querySelector('.drag-drop-button')
    if (fallbackButton && this.hasInputTarget) {
      fallbackButton.addEventListener('click', () => {
        this.inputTarget.click()
      })
    }
  }

  setupFilePond() {
    if (!this.hasInputTarget) {
      return
    }

    // Prevent duplicate FilePond instances
    if (this.pond) {
      this.pond.destroy()
      this.pond = null
    }

    // Check if FilePond is already initialized on this element
    if (this.inputTarget.filepond) {
      FilePond.destroy(this.inputTarget)
    }

    const maxFileSize = this.maxFileSizeValue || '10MB'
    
    // Handle file types - convert extensions to MIME types if needed
    let acceptedFileTypes
    if (this.acceptedFileTypesValue) {
      // If we have extensions from HTML, convert them to MIME types
      const extensionToMimeType = {
        '.pdf': 'application/pdf',
        '.doc': 'application/msword',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.xls': 'application/vnd.ms-excel',
        '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.txt': 'text/plain',
        '.jpeg': 'image/jpeg',
        '.jpg': 'image/jpeg',
        '.png': 'image/png',
        '.gif': 'image/gif',
        '.zip': 'application/zip',
        '.rar': 'application/x-rar-compressed'
      }
      
      const extensions = this.acceptedFileTypesValue.split(',')
      acceptedFileTypes = extensions.map(ext => extensionToMimeType[ext.trim()] || ext.trim()).filter(Boolean)
    } else {
      // Fallback MIME types with explicit PDF support
      acceptedFileTypes = [
        'application/pdf', // Ensure PDF is first
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/zip',
        'application/x-rar-compressed'
      ]
    }

    // Also accept file extensions directly for better compatibility
    const fileExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.jpeg', '.jpg', '.png', '.gif', '.zip', '.rar']
    
    // Combine MIME types and extensions for maximum compatibility
    const allAcceptedTypes = [...acceptedFileTypes, ...fileExtensions]

    // Track upload items
    this.uploadItems = new Map()

    // Create FilePond instance
    this.pond = FilePond.create(this.inputTarget, {
      server: {
        process: {
          url: this.urlValue,
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          onload: (response) => {
            try {
              const parsed = JSON.parse(response)
              if (parsed.status === 'success') {
                // Show success briefly, then reload
                setTimeout(() => window.location.reload(), 1500)
              }
              return response
            } catch (e) {
              return response
            }
          },
          onerror: (response) => {
            return response
          }
        }
      },

      // Configuration
      allowMultiple: true,
      name: 'files[]',
      credits: false,
      instantUpload: true,
      
      // Disable FilePond's default file display
      allowFileTypeValidation: false,
      allowFileSizeValidation: true,
      allowImagePreview: false,
      allowImageCrop: false,
      allowImageResize: false,
      allowRevert: false,
      allowRemove: false,
      
      // Disable FilePond's built-in error notifications
      allowBrowse: true,
      allowDrop: true,
      allowPaste: false,
      allowReplace: false,
      allowSync: false,
      
      // Disable FilePond's internal status display
      allowProcess: true,
      allowReorder: false,
      allowDirectoriesOnly: false,
      
      // Hide FilePond's default UI elements
      stylePanelLayout: 'compact',
      stylePanelAspectRatio: 'ignore',
      styleButtonRemoveItemPosition: 'left',
      styleButtonProcessItemPosition: 'right',
      
      // Portuguese labels
      labelIdle: 'Arraste ficheiros para aqui ou <span class="filepond--label-action">selecione ficheiros</span>',
      labelFileProcessing: 'A enviar...',
      labelFileProcessingComplete: 'Enviado',
      labelFileProcessingAborted: 'Cancelado',
      labelFileProcessingError: 'Erro no envio',
      labelTapToCancel: 'toque para cancelar',
      labelTapToRetry: 'toque para tentar novamente',
      labelTapToUndo: 'toque para desfazer',
      
      // Additional Portuguese labels
      labelInvalidField: 'Campo contém ficheiros inválidos',
      labelFileWaitingForSize: 'A aguardar tamanho',
      labelFileSizeNotAvailable: 'Tamanho não disponível',
      labelFileLoading: 'A carregar',
      labelFileLoadError: 'Erro durante carregamento',
      labelFileProcessingRevertError: 'Erro durante reversão',
      labelFileRemoveError: 'Erro durante remoção',
      labelButtonRemoveItem: 'Remover',
      labelButtonAbortItemLoad: 'Abortar',
      labelButtonRetryItemLoad: 'Repetir',
      labelButtonAbortItemProcessing: 'Cancelar',
      labelButtonUndoItemProcessing: 'Desfazer',
      labelButtonRetryItemProcessing: 'Repetir',
      labelButtonProcessItem: 'Carregar',
      
      // File validation labels in Portuguese
      labelFileTypeNotAllowed: 'Ficheiro de tipo não permitido',
      labelFileSizeTooBig: 'Ficheiro demasiado grande',
      labelFileSizeToSmall: 'Ficheiro demasiado pequeno',
      labelExpectedFileSizeTooBig: 'O ficheiro é demasiado grande',
      labelExpectedFileSizeToSmall: 'O ficheiro é demasiado pequeno',
      
      // File validation
      maxFileSize: maxFileSize,
      acceptedFileTypes: allAcceptedTypes,
      
      // Event handlers for custom progress display
      onaddfile: (error, file) => {
        if (error) {
          const fileName = file.file?.name || 'Unknown file'
          const errorMessage = this.getErrorMessage(error, fileName)
          this.showError('Erro no ficheiro', errorMessage, fileName)
        } else {
          this.clearErrors()
          this.showUploadProgress()
          this.createUploadItem(file)
        }
      },
      
      onprocessfilestart: (file) => {
        this.updateUploadItem(file.id, 'processing', 0)
      },
      
      onprocessfile: (error, file) => {
        if (!error) {
          this.updateUploadItem(file.id, 'complete', 100)
        } else {
          this.updateUploadItem(file.id, 'error', 0)
          
          const fileName = file.filename || file.file?.name || 'Unknown file'
          const errorMessage = this.getErrorMessage(error, fileName)
          this.showError('Erro no envio', errorMessage, fileName)
        }
      },
      
      onprocessfileprogress: (file, progress) => {
        this.updateUploadItem(file.id, 'processing', Math.round(progress * 100))
      },
      
      onprocessfileerror: (error, file) => {
        this.updateUploadItem(file.id, 'error', 0)
        
        const fileName = file.filename || file.file?.name || 'Unknown file'
        const errorMessage = this.getErrorMessage(error, fileName)
        this.showError('Erro no processamento', errorMessage, fileName)
      },
      
      onremovefile: (error, file) => {
        this.removeUploadItem(file.id)
        
        if (this.pond.getFiles().length === 0) {
          this.hideUploadProgress()
        }
      }
    })
  }

  showUploadProgress() {
    if (this.hasUploadProgressTarget) {
      this.uploadProgressTarget.classList.remove('hidden')
      
      const fallbackArea = this.element.querySelector('.drag-drop-fallback')
      if (fallbackArea) {
        fallbackArea.classList.add('hidden')
      }

      const existingFilesSection = document.querySelector('.existing-files')
      if (existingFilesSection) {
        existingFilesSection.classList.add('hidden')
      }
    }
  }

  hideUploadProgress() {
    if (this.hasUploadProgressTarget) {
      this.uploadProgressTarget.classList.add('hidden')
      
      const fallbackArea = this.element.querySelector('.drag-drop-fallback')
      if (fallbackArea) {
        fallbackArea.classList.remove('hidden')
      }

      const existingFilesSection = document.querySelector('.existing-files')
      if (existingFilesSection) {
        existingFilesSection.classList.remove('hidden')
      }
    }
  }

  createUploadItem(file) {
    if (!this.hasUploadListTarget) {
      return
    }

    if (this.uploadItems.has(file.id)) {
      return
    }

    const uploadItem = document.createElement('div')
    uploadItem.className = 'upload-item processing'
    uploadItem.dataset.fileId = file.id
    
    const fileName = file.filename || file.file?.name || 'Unknown file'
    
    uploadItem.innerHTML = `
      <div class="upload-item-icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      </div>
      <div class="upload-item-details">
        <div class="upload-item-name" style="font-size: 16px; font-weight: 600; margin-bottom: 8px;">
          ${fileName}
          <span class="upload-status-badge">A enviar</span>
        </div>
        <div class="upload-item-progress">
          <div class="upload-progress-bar">
            <div class="upload-progress-fill" style="width: 0%"></div>
          </div>
          <div class="upload-progress-text">0%</div>
        </div>
      </div>
    `
    
    this.uploadListTarget.appendChild(uploadItem)
    this.uploadItems.set(file.id, uploadItem)
  }

  updateUploadItem(fileId, state, progress) {
    const item = this.uploadItems.get(fileId)
    if (!item) {
      return
    }

    item.className = `upload-item ${state}`
    
    const progressFill = item.querySelector('.upload-progress-fill')
    const progressText = item.querySelector('.upload-progress-text')
    const statusBadge = item.querySelector('.upload-status-badge')
    
    if (progressFill) progressFill.style.width = `${progress}%`
    
    if (progressText && statusBadge) {
      switch (state) {
        case 'processing':
          progressText.textContent = `${progress}%`
          statusBadge.textContent = 'A enviar'
          statusBadge.className = 'upload-status-badge'
          break
        case 'complete':
          progressText.textContent = 'Completo'
          statusBadge.textContent = 'Enviado'
          statusBadge.className = 'upload-status-badge'
          this.updateUploadIcon(item, 'success')
          break
        case 'error':
          progressText.textContent = 'Erro'
          statusBadge.textContent = 'Falhou'
          statusBadge.className = 'upload-status-badge'
          this.updateUploadIcon(item, 'error')
          break
      }
    }
    
    this.checkGlobalUploadStatus()
  }

  updateUploadIcon(item, state) {
    const iconContainer = item.querySelector('.upload-item-icon')
    if (!iconContainer) return
    
    let iconSvg = ''
    switch (state) {
      case 'success':
        iconSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        `
        break
      case 'error':
        iconSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        `
        break
    }
    
    if (iconSvg) {
      iconContainer.innerHTML = iconSvg
    }
  }

  showGlobalUploadIndicator() {
    this.hideGlobalUploadIndicator()
    
    const indicator = document.createElement('div')
    indicator.className = 'upload-in-progress'
    indicator.id = 'global-upload-indicator'
    indicator.innerHTML = `
      <svg class="upload-in-progress-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
      </svg>
      <span class="upload-in-progress-text">A enviar ficheiros...</span>
    `
    
    document.body.appendChild(indicator)
  }

  hideGlobalUploadIndicator() {
    const existing = document.getElementById('global-upload-indicator')
    if (existing) {
      existing.remove()
    }
  }

  checkGlobalUploadStatus() {
    const processingItems = Array.from(this.uploadItems.values())
      .filter(item => item.classList.contains('processing'))
    
    if (processingItems.length === 0) {
      setTimeout(() => {
        this.hideGlobalUploadIndicator()
        
        this.hideUploadProgress()
      }, 2000)
    }
  }

  removeUploadItem(fileId) {
    const item = this.uploadItems.get(fileId)
    if (item) {
      item.remove()
      this.uploadItems.delete(fileId)
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  showError(title, message, fileName = null) {
    if (!this.hasErrorDisplayTarget || !this.hasErrorListTarget) {
      return
    }

    this.errorDisplayTarget.classList.remove('hidden')

    const errorId = 'error-' + Date.now() + Math.random().toString(36).substr(2, 9)
    const errorItem = document.createElement('div')
    errorItem.className = 'upload-error-item'
    errorItem.dataset.errorId = errorId
    
    errorItem.innerHTML = `
      <div class="upload-error-icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      </div>
      <div class="upload-error-content">
        <div class="upload-error-title">${title}${fileName ? ': ' + fileName : ''}</div>
        <div class="upload-error-message">${message}</div>
      </div>
      <button type="button" class="upload-error-dismiss" onclick="this.closest('.upload-error-item').remove(); if(!document.querySelectorAll('.upload-error-item').length) document.querySelector('[data-filepond-target=errorDisplay]').classList.add('hidden');" title="Dismiss error" aria-label="Dismiss error">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `
    
    this.errorListTarget.appendChild(errorItem)

    setTimeout(() => {
      const item = document.querySelector(`[data-error-id="${errorId}"]`)
      if (item) {
        item.remove()
        if (!this.errorListTarget.children.length) {
          this.errorDisplayTarget.classList.add('hidden')
        }
      }
    }, 8000)
  }

  clearErrors() {
    if (this.hasErrorListTarget) {
      this.errorListTarget.innerHTML = ''
    }
    if (this.hasErrorDisplayTarget) {
      this.errorDisplayTarget.classList.add('hidden')
    }
  }

  getErrorMessage(error, fileName) {
    const errorMessages = {
      'FILE_TOO_LARGE': 'O ficheiro é demasiado grande. Tamanho máximo permitido: 10MB.',
      'FILE_INVALID_TYPE': 'Tipo de ficheiro não suportado. Tipos permitidos: PDF, DOC, DOCX, XLS, XLSX, TXT, JPEG, PNG, GIF, ZIP, RAR.',
      'FILE_TOO_SMALL': 'O ficheiro é demasiado pequeno.',
      'FILE_COUNT_LIMIT_EXCEEDED': 'Número máximo de ficheiros excedido.',
      'FILE_SIZE_LIMIT_EXCEEDED': 'O ficheiro excede o limite de tamanho.',
      'FILE_TYPE_INVALID': 'Tipo de ficheiro inválido. Tipos permitidos: PDF, DOC, DOCX, XLS, XLSX, TXT, JPEG, PNG, GIF, ZIP, RAR.',
      'FILE_UPLOAD_FAILED': 'Falha no envio do ficheiro. Tente novamente.',
      'FILE_PROCESSING_FAILED': 'Erro ao processar o ficheiro.',
      'FILE_PROCESSING_ABORTED': 'Envio do ficheiro cancelado.',
      'FILE_PROCESSING_COMPLETE': 'Ficheiro enviado com sucesso.',
      'FILE_PROCESSING_ERROR': 'Erro durante o processamento do ficheiro.',
      
      'MAX_FILES': 'Número máximo de ficheiros atingido.',
      'FILE_IS_TOO_LARGE': 'O ficheiro é demasiado grande.',
      'FILE_IS_TOO_SMALL': 'O ficheiro é demasiado pequeno.',
      'FILE_TYPE_NOT_ALLOWED': 'Tipo de ficheiro não permitido.',
      'FILE_SIZE_TOO_BIG': 'O ficheiro excede o tamanho máximo permitido.',
      'FILE_SIZE_TOO_SMALL': 'O ficheiro é demasiado pequeno.',
      'INVALID_FILE_TYPE': 'Formato de ficheiro inválido.',
      'INVALID_FILE_SIZE': 'Tamanho de ficheiro inválido.',
      'UPLOAD_ERROR': 'Erro durante o carregamento.',
      'NETWORK_ERROR': 'Erro de conexão. Verifique a sua ligação à internet.',
      'SERVER_ERROR': 'Erro do servidor. Tente novamente mais tarde.',
      'TIMEOUT_ERROR': 'Tempo limite excedido. Tente novamente.',
      'VALIDATION_ERROR': 'Erro de validação do ficheiro.',
      'UNKNOWN_ERROR': 'Erro desconhecido. Tente novamente.'
    }

    let errorCode, errorMessage
    
    if (typeof error === 'string') {
      errorCode = error
      errorMessage = error
    } else if (error && typeof error === 'object') {
      errorCode = error.code || error.type || error.main || error.sub || 'UNKNOWN_ERROR'
      errorMessage = error.message || error.body || error.description || error.text || error.toString()
    } else {
      errorCode = 'UNKNOWN_ERROR'
      errorMessage = 'Erro desconhecido'
    }

    const friendlyMessage = errorMessages[errorCode]
    if (friendlyMessage) {
      return friendlyMessage
    }
    
    if (errorMessage && errorMessage !== '[object Object]' && errorMessage !== error.toString()) {
      return errorMessage
    }
    
    return `Erro no ficheiro: ${errorCode || 'erro desconhecido'}`
  }
} 
